import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Link as RouterLink } from 'react-router-dom';
import myActionsApi from '../../../../services/api/MyActionsApi';
import DocumentApi from '../../../../services/api/DocumentApi';
import { LoadingButton } from '@mui/lab';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
  Box,
  Dialog,
  Grid,
  IconButton,
  Skeleton,
  Stack,
  TextField,
  Typography,
  useTheme,
  Button,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import LinkIcon from '@mui/icons-material/Link';
import { FileDropzone } from '../../../../components/ui/FileDropzone';
import {
  autoCompleteDataFetcher,
  objectTypes,
} from '../../../../services/GenericService/GenericService';
import RemoteDataAutoComplete from '../../../../components/AutoComplete/CustomAutoComplete/RemoteDataAutoComplete';
import useAuth from '../../../../hooks/useAuth';
import ActionModalComment from './ActionModalComment';
import commonApi from '../../../../services/api/CommonApi';
import CheckIcon from '@mui/icons-material/Check';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { convertFileToBase64 } from '../../../../utils/fileTobase64';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { DialogTransition } from '../../../../components/ui/dialogs/DialogTransition';

const toDate = (dateString) => (dateString ? new Date(dateString) : null);

const initalActionObject = {
  ActionID: -1,
  Title: '',
  Description: '',
  Owner: null,
  BelongsTo: null,
  RecordID: null,
  DueDate: '',
  StatusName: '',
  ObjectName: ''
};

const MyActionsCardModal = ({
  actionID,
  onClose,
  open,
  belongsTo,
  recordID,
  sourceLink,
  dialogTitle,
  actionStatusChanged,
  newCommentAdded,
  canDownload,
  onClickDownloadFile,
  ...other
}) => {
  const [overrideActionID, setOverrideActionID] = useState(null);

  const currentActionID = overrideActionID ?? actionID;
  const isEditMode = currentActionID > 0;
  // console.log('currentActionID', currentActionID);
  // -- Hooks --
  const theme = useTheme();
  const { t } = useTranslation();
  const { dateFormat, user, isPetroHubUser } = useAuth();
  const formRef = useRef('formic_form');
  const { FirstName, LastName, UserID } = user;
  const loginUserID = `${UserID}`;
  const userFullname = `${FirstName} ${LastName}`;

  const [actionObject, setActionObject] = useState(initalActionObject);

  const [comments, setComments] = useState([]);
  const [actionFiles, setActionFiles] = useState([]);

  const [isLoadingCardData, setIsLoadingCardData] = useState(false);
  const [isLoadingComments, setIsLoadingComments] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  const isSaveEnabled =
    !isEditMode ||
    loginUserID?.toLowerCase() === actionObject?.CreatedBy?.toLowerCase();
  const isAllowToEditDetail =
    !isEditMode ||
    loginUserID?.toLowerCase() === actionObject?.CreatedBy?.toLowerCase();

  const statusName = actionObject.StatusName ?? '';
  const objectSourceLink = actionObject.SourceLink ?? '';
  const objectName = actionObject?.ObjectName ? actionObject.ObjectName : actionObject.RecordID;
  // console.log('actionObject', actionObject);
  // -- Hooks --

  // -- Function Handler(s) --
  const handleDetailsUpdate = useCallback((event) => {
    const { name, value } = event.target ?? event;
    setActionObject((prev) => ({ ...prev, [name]: value }));
  }, []);

  // Card Data -------------------------------------
  const loadActionCardData = useCallback(async () => {
    setIsLoadingCardData(true);

    if (!currentActionID || !open) {
      setIsLoadingCardData(false);
      return;
    }
    // console.log('Fetching data for action id:', currentActionID);
    try {
      const actionData = await myActionsApi.getActionByActionID({
        actionID: currentActionID,
      });

      if (actionData) {
        setActionObject(actionData);
        handleDetailsUpdate({
          name: 'StatusName',
          value: actionData.StatusName || '',
        });

        // const assignedToObj = {
        //   id: actionData.Owner,
        //   text: actionData.OwnerName,
        // };

        // const actionTypeObj = {
        //   id: actionData.ActionTypeID,
        //   text: actionData.ActionTypeName,
        // };

        // setSelectedAssignedTo(assignedToObj || null);
        // setSelectedActionType(actionTypeObj || null);
      }
    } catch (error) {
      console.log(error);
      toast.error(error);
    }
    setIsLoadingCardData(false);
  }, [currentActionID, handleDetailsUpdate, open]);

  useEffect(() => {
    loadActionCardData();
  }, [loadActionCardData]);
  // ---------------------------------------------------

  // Comments --------------------------------------------
  const actionCommentHandler = useCallback(async () => {
    setIsLoadingComments(true);

    if (!currentActionID || !open) {
      setIsLoadingComments(false);
      return;
    }

    try {
      const commentList = await commonApi.getNotes({
        belongTo: 'Action',
        recordID: `${currentActionID}`,
      });

      commentList.sort((c1, c2) => {
        if (!c1.CreatedOn && !c2.CreatedOn) {
          return 1;
        }

        return new Date(c2.CreatedOn) - new Date(c1.CreatedOn);
      });

      setComments(commentList ?? []);
    } catch (error) {
      console.log(error);
      toast.error(error);
    }

    setIsLoadingComments(false);
  }, [currentActionID, open]);

  useEffect(() => {
    actionCommentHandler();
  }, [actionCommentHandler]);

  // Fetch All Uploaded Files
  const refreshUploadedFilesList = useCallback(async () => {
    if (currentActionID > 0) {
      const allUploades = await DocumentApi.getDocumentByRecordID({ belongsTo: 'Action', recordID: currentActionID });
      const newAllUploades = allUploades?.map((v) => {
        v['name'] = v?.FileName;
        return v;
      });
      setActionFiles(newAllUploades);
    }
  }, [currentActionID]);

  useEffect(() => {
    refreshUploadedFilesList();
  }, [refreshUploadedFilesList]);
  // ---------------------------------------------------

  const handleActionStatusChange = async (api) => {
    setIsUpdatingStatus(true);

    try {
      const { StatusName, AcceptedOn, AcceptedBy, CompletedOn, CompletedBy } =
        await api({
          actionID: currentActionID,
        });

      handleDetailsUpdate({
        name: 'StatusName',
        value: StatusName || '',
      });

      if (actionStatusChanged) {
        actionStatusChanged({
          ...actionObject,
          StatusName: StatusName || '',
          AcceptedOn,
          AcceptedBy,
          CompletedOn,
          CompletedBy,
        });
      }

      toast.success(t('Toast_ActionStatusUpdatedSuccessfully'));
    } catch (error) {
      console.log(error);
      toast.error(t('Error_Message_SomethingWentWrong'));
    }

    setIsUpdatingStatus(false);
  };
  const handleStartAction = async () => {
    handleActionStatusChange(myActionsApi.startAction);
  };

  const handleCompleteAction = async () => {
    handleActionStatusChange(myActionsApi.completeAction);
  };

  const handleReopenAction = async () => {
    handleActionStatusChange(myActionsApi.reopenAction);
  };

  const handleSave = async () => {
    const { ActionType, AssignedTo, Description,
      DueDate, Title } = formRef.current?.values;
    // To Validate the Add/Edit Model Form
    if (!ActionType?.id || !AssignedTo?.id || !DueDate || !Title) {
      formRef.current.handleSubmit();
      // toast.error('Please fill all mandatory fields');
      return;
    }
    try {
      setIsSubmitting(true);

      if (currentActionID > 0) {
        const requestDataObj = {
          ...actionObject,
          Owner: AssignedTo?.id ? `${AssignedTo.id}` : null,
          ActionTypeID: ActionType?.id,
          ActionTypeName: ActionType.text,
          Description,
          DueDate: toDate(DueDate).toISOString(),
          Title
        };

        await myActionsApi.updateActionInfo(requestDataObj);
        toast.success('Update action successfully!');

        if (
          loginUserID?.toLowerCase() === requestDataObj?.Owner?.toLowerCase()
        ) {
          if (actionStatusChanged) {
            actionStatusChanged({ ...requestDataObj });
          }
        }
      } else {
        const requestDataObj = {
          ...actionObject,
          ActionID: -1,
          Owner: AssignedTo?.id ? `${AssignedTo.id}` : null,
          ActionTypeID: ActionType?.id,
          ActionTypeName: ActionType?.text,
          StatusName: 'New',
          CreatedOn: new Date().toISOString(),
          CreatedBy: loginUserID,
          BelongsTo: belongsTo ?? null,
          RecordID: recordID ?? null,
          SourceLink: sourceLink ?? null,
          Description,
          DueDate: toDate(DueDate).toISOString(),
          Title
        };

        const newActionId = await myActionsApi.addNewAction(requestDataObj);
        toast.success(t('Toast_ActionAddedSuccessfully'));

        setOverrideActionID(newActionId);

        if (
          loginUserID?.toLowerCase() === requestDataObj?.Owner?.toLowerCase()
        ) {
          if (actionStatusChanged) {
            actionStatusChanged({ ...requestDataObj, ActionID: newActionId });
          }
        }
      }

      setIsSubmitting(false);
    } catch (error) {
      console.log(error?.response?.data?.message);
      setIsSubmitting(false);
      toast.error(t('Error_Message_SomethingWentWrong'));
    }
  };

  const handleClose = () => {
    setActionObject({ ...initalActionObject });
    setOverrideActionID(null);
    setActionFiles([]);
    setComments([]);
    formRef?.current?.resetForm();
    onClose();
  };

  // Upload files
  const handleDropFile = async (newFiles) => {
    try {
      const files = await convertFileToBase64(newFiles);
      const addOnFileDetails = files?.map((file) => ({
        Title: actionObject.Title,
        FileName: file.name,
        Description: actionObject.Description,
        Active: true,
        BelongsTo: 'Action',
        RecordID: `${currentActionID}`,
        DocumentTypeID: 2,
        FileContent: file?.content?.split(',')[1]
      }));

      const uploades = await DocumentApi.addNewDocumentInBase64(addOnFileDetails);
      const customUploades = uploades.map((v) => { v['name'] = v?.FileName; return v; });
      setActionFiles((prevFiles) => [...prevFiles, ...customUploades]);
      toast.success(t('Toast_FilesUploadedSuccessfully'));
    } catch (error) {
      toast.error(t('Error_Message_SomethingWentWrong'));
      console.log(error);
    }
  };

  // Remove File
  const handleRemoveFile = async (file) => {
    try {
      const isDeleted = await DocumentApi.deleteDocumentByDocumentID(file?.DocumentID);

      if (isDeleted) {
        setActionFiles((prev) => prev.filter((_file) => _file.DocumentID !== file?.DocumentID));
        toast.success(t('Toast_FileRemovedSuccessfully'));
      }
    } catch (error) {
      toast.error(t('Error_Message_SomethingWentWrong'));
      console.log(error);
    }
  };

  // Download File
  const handleDownloadFile = async ({ DocumentID, FileName }) => {
    try {
      const dataBytes = await DocumentApi.getDocumentContentByDocumentID({ documentId: DocumentID });
      if (dataBytes) {
        const url = window.URL.createObjectURL(
          dataBytes
        );

        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${FileName}`);
        document.body.appendChild(link);
        link.click();
        window.URL.revokeObjectURL(url);
        link.remove();

        toast.success(t('Toast_FileDownloadedSuccessfully'));
      }
    } catch (error) {
      toast.error(t('Error_Message_SomethingWentWrong'));
      console.log(error);
    }
  };

  const handleRemoveAllFiles = () => {
    setActionFiles([]);
  };

  const handleAddComment = useCallback(
    async (commentText) => {
      try {
        const newComment = {
          belongTo: 'Action',
          recordID: `${currentActionID}`,
          content: commentText,
          createdBy: userFullname,
          createdOn: new Date().toISOString(),
        };
        const newNote = await commonApi.addNote(newComment);

        if (newNote?.NoteID > 0) {
          actionCommentHandler();

          if (newCommentAdded) {
            newCommentAdded(newNote);
          }
        }
      } catch (error) {
        console.log(error);
      }
    },
    [actionCommentHandler, currentActionID, userFullname, newCommentAdded]
  );

  // - Render Content(s) --
  const attachmentContents = (
    <Box sx={{ mt: 3 }}>
      <Typography
        color="primary"
        component="h4"
        sx={{
          fontWeight: 600,
          mb: 1,
        }}
        variant="overline"
      >
        {t('Header_Attachments')}
      </Typography>

      <FileDropzone
        files={actionFiles}
        maxFiles={5}
        disabled={actionFiles.length > 5}
        onDrop={handleDropFile}
        onRemove={handleRemoveFile}
        onRemoveAll={handleRemoveAllFiles}
        onClickDownload={handleDownloadFile}
        canDownload
        isHideSelectFileText
        hideRemoveAll
        hideUpload
      />
    </Box>
  );

  const initializeModelForm = {
    ActionType: actionObject?.ActionTypeID ? { id: actionObject?.ActionTypeID, text: actionObject?.ActionTypeName }
      : null,
    Title: actionObject.Title,
    Description: actionObject.Description,
    AssignedTo: actionObject?.Owner ? { id: actionObject?.Owner, text: actionObject?.OwnerName } : null,
    DueDate: actionObject.DueDate && toDate(actionObject.DueDate).toISOString(),
  };

  // - Render Content(s) --
  return (
    <Dialog
      fullWidth
      keepMounted
      maxWidth="md"
      onClose={onClose}
      open={open}
      TransitionComponent={DialogTransition}
      {...other}
    >
      <Grid container>
        <Grid item xs={12} sm={9}>
          <Box
            sx={{
              py: 4,
              px: 3,
            }}
          >
            <Stack
              direction="row"
              justifyContent="flex-start"
              alignItems="center"
              spacing={1}
            >
              <EditIcon color="primary" fontSize="small" />
              <Typography variant="h5">
                {dialogTitle ?? (isEditMode ? 'Edit Action '.concat(objectSourceLink && `for ${objectName}`)
                  : t('Btn_AddActions'))}
              </Typography>
            </Stack>

            <Formik
              key={actionObject.Title}
              innerRef={formRef}
              initialValues={initializeModelForm}
              validationSchema={Yup.object().shape({
                ActionType: Yup.object()
                  .shape({
                    id: Yup.string(),
                    text: Yup.string(),
                  })
                  .nullable()
                  .required(t('Error_ActionTypeRequired')),
                AssignedTo: Yup.object()
                  .shape({
                    id: Yup.string(),
                    text: Yup.string(),
                  })
                  .nullable()
                  .required(t('Error_AssignedToRequired')),
                Title: Yup.string().required(t('Error_TitleRequired')),
                DueDate: Yup.string().required(t('Error_DueDateRequired'))
              })}
            >
              {({ values, setFieldValue, touched, errors }) => (
                <form>
                  <Grid container spacing={2.5} sx={{ mt: 1.5 }}>
                    {isLoadingCardData ? (
                      <Grid item xs={4} style={{ paddingTop: 0 }}>
                        <Skeleton animation="wave" height={50} />
                      </Grid>
                    ) : (
                      <Grid item xs={12} md={4}>
                        <RemoteDataAutoComplete
                          required
                          serverFilter
                          pageSize={50}
                          label={t('Label_ActionType')}
                          name="ActionType"
                          objectType={objectTypes.masterData}
                          fetcher={autoCompleteDataFetcher}
                          selectedItem={values?.ActionType}
                          onSelectedItemChanged={(newValue, type, name) => {
                            setFieldValue(name, newValue);
                          }}
                          extraFilter={isPetroHubUser ? { mdTypeName: 'CylinderActionType' } : { mdTypeName: 'ActionType' }}
                          disabled={!isAllowToEditDetail}
                          error={Boolean(touched.ActionType && errors.ActionType)}
                          helperText={touched.ActionType && errors.ActionType}
                        />
                      </Grid>
                    )}

                    {isLoadingCardData ? (
                      <Grid item xs={8} style={{ paddingTop: 0 }}>
                        <Skeleton animation="wave" height={50} />
                      </Grid>
                    ) : (
                      <Grid item xs={12} md={8}>
                        <TextField
                          fullWidth
                          required
                          label={t('Label_ActionTitle')}
                          disabled={!isAllowToEditDetail}
                          value={values.Title}
                          size="small"
                          name="Title"
                          onChange={(e) => {
                            setFieldValue('Title', e.target.value);
                          }}
                          error={Boolean(touched.Title && errors.Title)}
                          helperText={touched.Title && errors.Title}
                        />
                      </Grid>
                    )}

                    {isLoadingCardData ? (
                      <Grid item xs={12} style={{ paddingTop: 0 }}>
                        <Skeleton animation="wave" height={150} />
                      </Grid>
                    ) : (
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          multiline
                          rows={4}
                          disabled={!isAllowToEditDetail}
                          value={values.Description}
                          size="small"
                          label={t('Label_Description')}
                          name="Description"
                          onChange={(e) => {
                            setFieldValue('Description', e.target.value);
                          }}
                        />
                      </Grid>
                    )}

                    {isLoadingCardData ? (
                      <Grid item xs={6} style={{ paddingTop: 0 }}>
                        <Skeleton animation="wave" height={50} />
                      </Grid>
                    ) : (
                      <Grid item xs={12} md={6}>
                        <RemoteDataAutoComplete
                          required
                          serverFilter
                          pageSize={50}
                          label={t('Label_AssignedTo')}
                          name="AssignedTo"
                          objectType={objectTypes.actionOwner}
                          fetcher={autoCompleteDataFetcher}
                          selectedItem={values?.AssignedTo}
                          onSelectedItemChanged={(newValue, type, name) => {
                            setFieldValue(name, newValue);
                          }}
                          disabled={!isAllowToEditDetail}
                          error={Boolean(touched.AssignedTo && errors.AssignedTo)}
                          helperText={touched.AssignedTo && errors.AssignedTo}
                        />
                      </Grid>
                    )}

                    {isLoadingCardData ? (
                      <Grid item xs={6} style={{ paddingTop: 0 }}>
                        <Skeleton animation="wave" height={50} />
                      </Grid>
                    ) : (
                      <Grid item xs={12} md={6}>
                        <DatePicker
                          label={t('Label_DueDate')}
                          name="ActionDueDate"
                          disabled={!isAllowToEditDetail}
                          inputFormat={dateFormat}
                          value={values.DueDate}
                          onChange={(newValue) => {
                            setFieldValue('DueDate', newValue || '');
                          }}
                          disableMaskedInput
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              fullWidth
                              required
                              size="small"
                              error={Boolean(touched.DueDate && errors.DueDate)}
                              helperText={touched.DueDate && errors.DueDate}
                            />
                          )}
                        />
                      </Grid>
                    )}

                    {isEditMode && (
                      <ActionModalComment
                        addComment={handleAddComment}
                        comments={comments}
                        loadingComments={isLoadingComments}
                      />
                    )}
                  </Grid>
                </form>
              )}
            </Formik>
          </Box>
        </Grid>

        <Grid item xs={12} sm={3}>
          <Box
            sx={{
              backgroundColor: theme.palette.board.column,
              px: 3,
              py: 4,
              height: '100%',
            }}
          >
            {onClose ? (
              <IconButton
                size="small"
                color="primary"
                onClick={handleClose}
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: 8,
                }}
              >
                <CloseIcon />
              </IconButton>
            ) : null}

            <Box sx={{ mt: 3, width: '100%' }}>
              <Stack
                direction="column"
                alignItems="stretch"
                justifyContent="flex-end"
                spacing={1}
              >
                <Typography
                  color="primary"
                  component="h4"
                  sx={{
                    fontWeight: 600,
                  }}
                  variant="overline"
                >
                  {t('Header_Actions')}
                </Typography>

                {isLoadingCardData ? (
                  <Skeleton animation="wave" height={30} />
                ) : (
                  <>
                    {isEditMode && statusName === 'New' && (
                      <LoadingButton
                        fullWidth
                        color="primary"
                        size="small"
                        startIcon={<PlayArrowIcon fontSize="small" />}
                        variant="contained"
                        onClick={handleStartAction}
                        loading={isUpdatingStatus}
                        loadingPosition="start"
                      >
                        {t('Btn_Start')}
                      </LoadingButton>
                    )}

                    {isEditMode && statusName === 'InProgress' && (
                      <LoadingButton
                        fullWidth
                        color="primary"
                        size="small"
                        startIcon={<CheckIcon fontSize="small" />}
                        variant="contained"
                        onClick={handleCompleteAction}
                        loading={isUpdatingStatus}
                        loadingPosition="start"
                      >
                        {t('Btn_Complete')}
                      </LoadingButton>
                    )}

                    {isEditMode && statusName === 'Completed' && (
                      <LoadingButton
                        fullWidth
                        color="primary"
                        size="small"
                        startIcon={<AutorenewIcon fontSize="small" />}
                        variant="contained"
                        onClick={handleReopenAction}
                        loading={isUpdatingStatus}
                        loadingPosition="start"
                      >
                        {t('Btn_ReOpen')}
                      </LoadingButton>
                    )}
                  </>
                )}

                {isLoadingCardData ? (
                  <Skeleton animation="wave" height={30} />
                ) : (
                  <>
                    {isSaveEnabled && (
                      <LoadingButton
                        fullWidth
                        color="primary"
                        size="small"
                        startIcon={<SaveIcon fontSize="small" />}
                        variant="contained"
                        onClick={handleSave}
                        loading={isSubmitting}
                        loadingPosition="start"
                        disabled={isSubmitting}
                      >
                        {t('Btn_Save')}
                      </LoadingButton>
                    )}
                  </>
                )}

                {objectSourceLink && (
                  <Button
                    variant="text"
                    size="small"
                    sx={{ py: 0.5, pt: 2 }}
                    LinkComponent={RouterLink}
                    to={objectSourceLink}
                    startIcon={<LinkIcon fontSize="small" />}
                  >
                    {objectName}
                  </Button>
                )}
              </Stack>
            </Box>

            {isEditMode && attachmentContents}
          </Box>
        </Grid>
      </Grid>
    </Dialog>
  );
};

export default React.memo(MyActionsCardModal);
